import { useCallback, useEffect, useRef, useState } from "react";
import {
  createPubMissionBingo,
  getPubMissionBingoDetail,
  MissionBingoColumnType,
  MissionBingoRowRewardDetailType,
  resetPubMissionBingoProgress,
  SellerBingoStageRewardRequest,
  SellerPubMissionBingoRequest,
  updatePubMissionBingo,
} from "../../../api/pub-mission-bingo";

export interface BingoCellForm {
  key: string;
  row: number;
  column: number;
  name: string;
  type: MissionBingoColumnType;
  count: string;
}

export interface BingoStageRewardForm {
  key: string;
  type: MissionBingoRowRewardDetailType;
  count?: number;
  name?: string;
}

export interface BingoStageForm {
  key: string;
  row: string;
  rewards: BingoStageRewardForm[];
}

export interface BingoFormState {
  name: string;
  startDate: string;
  endDate: string;
  column: 3 | 4;
  cells: BingoCellForm[];
  stages: BingoStageForm[];
}

const createStageReward = (
  type: MissionBingoRowRewardDetailType = MissionBingoRowRewardDetailType.POINT,
): BingoStageRewardForm => ({
  key: `${Date.now()}-${Math.random()}`,
  type,
  count: type === MissionBingoRowRewardDetailType.POINT ? 1 : undefined,
  name: type === MissionBingoRowRewardDetailType.OTHER ? "" : undefined,
});

const createStage = (row = "1"): BingoStageForm => ({
  key: `${Date.now()}-${Math.random()}`,
  row,
  rewards: [createStageReward()],
});

const createCells = (size: 3 | 4): BingoCellForm[] => {
  const cells: BingoCellForm[] = [];
  for (let row = 0; row < size; row++) {
    for (let column = 0; column < size; column++) {
      cells.push({
        key: `${row}-${column}`,
        row,
        column,
        name: "",
        type: MissionBingoColumnType.ONE_OFF,
        count: "1",
      });
    }
  }
  return cells;
};

export const createInitialBingoFormState = (): BingoFormState => ({
  name: "",
  startDate: "",
  endDate: "",
  column: 3,
  cells: createCells(3),
  stages: [createStage()],
});

const mapDetailToForm = (
  detail: Awaited<ReturnType<typeof getPubMissionBingoDetail>>,
): BingoFormState => {
  const column = detail.column === 4 ? 4 : 3;
  const sortedCells = [...detail.cells].sort((a, b) => {
    if (a.row !== b.row) return a.row - b.row;
    return a.column - b.column;
  });

  return {
    name: detail.name,
    startDate: detail.startDate,
    endDate: detail.endDate,
    column,
    cells:
      sortedCells.length > 0
        ? sortedCells.map((cell) => ({
            key: `${cell.row}-${cell.column}`,
            row: cell.row,
            column: cell.column,
            name: cell.name,
            type: cell.type,
            count: cell.count ? String(cell.count) : "1",
          }))
        : createCells(column),
    stages:
      detail.stages.length > 0
        ? detail.stages.map((stage, stageIndex) => ({
            key: `stage-${stageIndex}-${stage.row}`,
            row: String(stage.row),
            rewards: stage.rewards.map((reward, rewardIndex) => ({
              key: `reward-${stageIndex}-${rewardIndex}`,
              type: reward.type,
              count: reward.count,
              name: reward.name,
            })),
          }))
        : [createStage()],
  };
};

export const getMaxLineCount = (column: 3 | 4) => (column === 3 ? 8 : 10);

export const getCellDisplayNumber = (row: number, column: number, size: number) =>
  row * size + column + 1;

export const useBingoForm = ({
  pubId,
  bingoId,
  onSuccess,
  onClose,
}: {
  pubId: number;
  bingoId?: number;
  onSuccess: () => Promise<void>;
  onClose: () => void;
}) => {
  const isEdit = Boolean(bingoId);
  const [form, setForm] = useState<BingoFormState>(createInitialBingoFormState);
  const [canEdit, setCanEdit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [selectedCellKey, setSelectedCellKey] = useState<string | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const loadDetail = useCallback(async () => {
    if (!bingoId || !pubId) {
      setForm(createInitialBingoFormState());
      setCanEdit(true);
      setSelectedCellKey("0-0");
      return;
    }

    setLoading(true);
    try {
      const detail = await getPubMissionBingoDetail(bingoId, pubId);
      const nextForm = mapDetailToForm(detail);
      setForm(nextForm);
      setCanEdit(detail.canEdit);
      setSelectedCellKey(nextForm.cells[0]?.key ?? null);
    } catch (error: any) {
      alert(error?.message || "빙고 정보를 불러오지 못했습니다.");
      onCloseRef.current();
    } finally {
      setLoading(false);
    }
  }, [bingoId, pubId]);

  useEffect(() => {
    if (!bingoId || !pubId) {
      setForm(createInitialBingoFormState());
      setCanEdit(true);
      setSelectedCellKey("0-0");
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const detail = await getPubMissionBingoDetail(bingoId, pubId);
        if (cancelled) return;
        const nextForm = mapDetailToForm(detail);
        setForm(nextForm);
        setCanEdit(detail.canEdit);
        setSelectedCellKey(nextForm.cells[0]?.key ?? null);
      } catch (error: any) {
        if (cancelled) return;
        alert(error?.message || "빙고 정보를 불러오지 못했습니다.");
        onCloseRef.current();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [bingoId, pubId]);

  const resetProgress = useCallback(async () => {
    if (!bingoId || !pubId) return;

    setResetting(true);
    try {
      await resetPubMissionBingoProgress(bingoId, pubId, {
        memo: "빙고 진행 상황을 초기화합니다",
      });
      await loadDetail();
      await onSuccess();
      alert("빙고 진행 상황이 초기화되었습니다.");
    } catch (error: any) {
      alert(error?.message || "진행 초기화에 실패했습니다.");
      throw error;
    } finally {
      setResetting(false);
    }
  }, [bingoId, loadDetail, onSuccess, pubId]);

  useEffect(() => {
    if (!selectedCellKey && form.cells.length > 0) {
      setSelectedCellKey(form.cells[0].key);
    }
  }, [form.cells, selectedCellKey]);

  const updateField = useCallback(
    <K extends keyof BingoFormState>(key: K, value: BingoFormState[K]) => {
      setForm((prev) => {
        if (key === "column" && value !== prev.column) {
          const nextSize = value as 3 | 4;
          return {
            ...prev,
            column: nextSize,
            cells: createCells(nextSize),
            stages: prev.stages.length ? prev.stages : [createStage()],
          };
        }
        return { ...prev, [key]: value };
      });

      if (key === "column") {
        setSelectedCellKey("0-0");
      }
    },
    [],
  );

  const updateCell = useCallback(
    (key: string, patch: Partial<BingoCellForm>) => {
      setForm((prev) => ({
        ...prev,
        cells: prev.cells.map((cell) =>
          cell.key === key ? { ...cell, ...patch } : cell,
        ),
      }));
    },
    [],
  );

  const addStage = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      stages: [...prev.stages, createStage(String(prev.stages.length + 1))],
    }));
  }, []);

  const removeStage = useCallback((key: string) => {
    setForm((prev) => ({
      ...prev,
      stages:
        prev.stages.length <= 1
          ? prev.stages
          : prev.stages.filter((stage) => stage.key !== key),
    }));
  }, []);

  const updateStage = useCallback((key: string, patch: Partial<BingoStageForm>) => {
    setForm((prev) => ({
      ...prev,
      stages: prev.stages.map((stage) =>
        stage.key === key ? { ...stage, ...patch } : stage,
      ),
    }));
  }, []);

  const addStageReward = useCallback((stageKey: string) => {
    setForm((prev) => ({
      ...prev,
      stages: prev.stages.map((stage) => {
        if (stage.key !== stageKey) return stage;
        const hasPoint = stage.rewards.some(
          (reward) => reward.type === MissionBingoRowRewardDetailType.POINT,
        );
        return {
          ...stage,
          rewards: [
            ...stage.rewards,
            createStageReward(
              hasPoint
                ? MissionBingoRowRewardDetailType.OTHER
                : MissionBingoRowRewardDetailType.POINT,
            ),
          ],
        };
      }),
    }));
  }, []);

  const removeStageReward = useCallback((stageKey: string, rewardKey: string) => {
    setForm((prev) => ({
      ...prev,
      stages: prev.stages.map((stage) => {
        if (stage.key !== stageKey) return stage;
        return {
          ...stage,
          rewards:
            stage.rewards.length <= 1
              ? stage.rewards
              : stage.rewards.filter((reward) => reward.key !== rewardKey),
        };
      }),
    }));
  }, []);

  const updateStageReward = useCallback(
    (
      stageKey: string,
      rewardKey: string,
      patch: Partial<SellerBingoStageRewardRequest>,
    ) => {
      setForm((prev) => ({
        ...prev,
        stages: prev.stages.map((stage) => {
          if (stage.key !== stageKey) return stage;
          return {
            ...stage,
            rewards: stage.rewards.map((reward) =>
              reward.key === rewardKey ? { ...reward, ...patch } : reward,
            ),
          };
        }),
      }));
    },
    [],
  );

  const validate = useCallback((): string | null => {
    if (!form.name.trim()) return "빙고명을 입력해주세요.";
    if (!form.startDate || !form.endDate) return "시작일/종료일을 입력해주세요.";

    const expectedCount = form.column * form.column;
    if (form.cells.length !== expectedCount) {
      return `빙고판은 ${expectedCount}칸이 모두 필요합니다.`;
    }

    for (const cell of form.cells) {
      if (!cell.name.trim()) {
        return `칸 ${getCellDisplayNumber(cell.row, cell.column, form.column)}의 제목을 입력해주세요.`;
      }
      if (
        cell.type === MissionBingoColumnType.CUMULATIVE &&
        (!cell.count || Number(cell.count) <= 0)
      ) {
        return `칸 ${getCellDisplayNumber(cell.row, cell.column, form.column)}의 목표 횟수를 입력해주세요.`;
      }
    }

    const maxLineCount = getMaxLineCount(form.column);
    const stageRows = new Set<number>();

    for (const stage of form.stages) {
      const stageRow = Number(stage.row);
      if (!stageRow || stageRow < 1 || stageRow > maxLineCount) {
        return `보상 단계 줄 수는 1~${maxLineCount}만 가능합니다.`;
      }
      if (stageRows.has(stageRow)) {
        return "보상 단계 줄 수가 중복되었습니다.";
      }
      stageRows.add(stageRow);

      if (!stage.rewards.length) {
        return "각 보상 단계에는 보상이 1개 이상 필요합니다.";
      }

      const pointRewards = stage.rewards.filter(
        (reward) => reward.type === MissionBingoRowRewardDetailType.POINT,
      );
      if (pointRewards.length > 1) {
        return "보상 단계마다 매장이용권은 1개만 등록할 수 있습니다.";
      }
      if (
        pointRewards.length === 1 &&
        (!pointRewards[0].count || pointRewards[0].count <= 0)
      ) {
        return "매장이용권 지급 수량을 입력해주세요.";
      }

      for (const reward of stage.rewards) {
        if (
          reward.type === MissionBingoRowRewardDetailType.OTHER &&
          !reward.name?.trim()
        ) {
          return "기타 보상 이름을 입력해주세요.";
        }
      }
    }

    return null;
  }, [form]);

  const buildRequest = useCallback((): SellerPubMissionBingoRequest => {
    return {
      pubId,
      name: form.name.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
      column: form.column,
      cells: form.cells.map((cell) => ({
        row: cell.row,
        column: cell.column,
        name: cell.name.trim(),
        type: cell.type,
        count:
          cell.type === MissionBingoColumnType.CUMULATIVE
            ? Number(cell.count)
            : undefined,
      })),
      stages: form.stages.map((stage) => ({
        row: Number(stage.row),
        rewards: stage.rewards.map(({ type, count, name }) => ({
          type,
          count:
            type === MissionBingoRowRewardDetailType.POINT
              ? Number(count)
              : undefined,
          name:
            type === MissionBingoRowRewardDetailType.OTHER
              ? name?.trim()
              : undefined,
        })),
      })),
    };
  }, [form, pubId]);

  const submit = useCallback(async () => {
    if (!canEdit) {
      alert("진행 중인 유저가 있어 빙고를 수정할 수 없습니다.");
      return;
    }

    const errorMessage = validate();
    if (errorMessage) {
      alert(errorMessage);
      return;
    }

    setSubmitting(true);
    try {
      const request = buildRequest();
      if (isEdit && bingoId) {
        await updatePubMissionBingo(bingoId, request);
      } else {
        await createPubMissionBingo(request);
      }
      await onSuccess();
      onCloseRef.current();
    } catch (error: any) {
      alert(error?.message || "저장에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  }, [bingoId, buildRequest, canEdit, isEdit, onSuccess, validate]);

  const selectedCell =
    form.cells.find((cell) => cell.key === selectedCellKey) ?? null;

  return {
    form,
    isEdit,
    canEdit,
    loading,
    submitting,
    resetting,
    selectedCell,
    selectedCellKey,
    setSelectedCellKey,
    updateField,
    updateCell,
    addStage,
    removeStage,
    updateStage,
    addStageReward,
    removeStageReward,
    updateStageReward,
    submit,
    resetProgress,
  };
};
